/**
 * Tile swatches (`projects/terrain-tile-art.md` 5.2): one small picture per tile, painted by the
 * same painter as the board and cached for the session, for the legend, the TERRAIN window's
 * palette and the authoring ghosts. A custom cell gets one per property set, on demand.
 *
 * Two shapes, because the two uses want different pictures:
 * - `chip`: a 2×2 patch of the tile on the map ground, cropped half a cell outside it, so the lit
 *   rim, the hazard glow and a raised wall's shadow all show. The legend and the palette use it.
 * - `fill`: the tile to every edge (the region runs off the board), for tiling across a painted
 *   preview cell by cell. Rims sit off the picture; the ghost's own outline carries the extent.
 *
 * Neither carries grid crosses. Edge walls get a swatch too ({@link wallSwatchScene}), so the legend's
 * wall rows show the wall the painter draws.
 */
import type { EdgeTerrain, TerrainCell, TerrainMap } from "@lancer-vtt/domain";
import { CELL, type EdgeVisual } from "../combatCells";
import { boardRect, regionKey } from "./geometry";
import { type Materials, sharedMaterials } from "./materials";
import { chunkPixelSize, paintChunk } from "./paint";
import { type TerrainScene, prepareScene } from "./scene";
import { type GroundId, DEFAULT_GROUND } from "./tileArt";

export type SwatchShape = "chip" | "fill";

/** Device px per board px a swatch paints at. A chip is 120 px, a 40 CSS px icon at dpr 3. */
const SWATCH_RESOLUTION = 1;

/** The scene a swatch paints and the board-px square it keeps. */
export function swatchScene(cell: TerrainCell, opts: { shape: SwatchShape; ground?: GroundId }): { scene: TerrainScene; crop: { x: number; y: number; size: number } } {
  const ground = opts.ground ?? DEFAULT_GROUND;
  const terrain: TerrainMap = {};
  if (opts.shape === "fill") {
    for (let y = 0; y < 2; y++) for (let x = 0; x < 2; x++) terrain[`${x},${y}`] = cell;
    return { scene: { board: { width: 2, height: 2 }, terrain, edges: {}, ground, grid: false }, crop: { x: 0, y: 0, size: 2 * CELL } };
  }
  for (let y = 1; y < 3; y++) for (let x = 1; x < 3; x++) terrain[`${x},${y}`] = cell;
  return { scene: { board: { width: 4, height: 4 }, terrain, edges: {}, ground, grid: false }, crop: { x: CELL / 2, y: CELL / 2, size: 3 * CELL } };
}

/** The flags that draw each wall class (`combatCells.ts`'s `edgeTreatment`, inverted). */
const WALL_FLAGS: Record<EdgeVisual, EdgeTerrain> = {
  full: { blocksMovement: true, blocksSight: true },
  low: { blocksMovement: true },
  "low-soft": { blocksMovement: true, cover: "soft" },
  "low-hard": { blocksMovement: true, cover: "hard" },
};

/** A wall class's swatch scene: the wall on the line between two columns of open ground. */
export function wallSwatchScene(visual: EdgeVisual, ground: GroundId = DEFAULT_GROUND): { scene: TerrainScene; crop: { x: number; y: number; size: number } } {
  const edges = { "0,0|1,0": WALL_FLAGS[visual], "0,1|1,1": WALL_FLAGS[visual] };
  return { scene: { board: { width: 2, height: 2 }, terrain: {}, edges, ground, grid: false }, crop: { x: 0, y: 0, size: 2 * CELL } };
}

/** What a swatch pictures: a tile in one of its shapes, or a wall class. */
export type SwatchSpec = { kind: "tile"; cell: TerrainCell; shape: SwatchShape; ground?: GroundId } | { kind: "wall"; visual: EdgeVisual; ground?: GroundId };

/** The cache key: the ground, the shape, and what the painter groups cells by. */
export function swatchKey(spec: SwatchSpec): string {
  const ground = spec.ground ?? DEFAULT_GROUND;
  return spec.kind === "wall" ? `${ground}|wall|${spec.visual}` : `${ground}|${spec.shape}|${regionKey(spec.cell)}`;
}

/** A painted swatch: a bitmap for the board, an object URL for the DOM. */
export type Swatch = { bitmap: ImageBitmap; url: string; size: number };

type SwatchPainter = (spec: SwatchSpec) => Promise<Swatch>;

/** Paint one swatch with the board's painter. Browser only (`OffscreenCanvas`). */
async function paintSwatch(materials: Materials, spec: SwatchSpec): Promise<Swatch> {
  const { scene, crop } = spec.kind === "wall" ? wallSwatchScene(spec.visual, spec.ground) : swatchScene(spec.cell, spec);
  const rect = boardRect(scene.board);
  const full = chunkPixelSize(rect, SWATCH_RESOLUTION);
  const board = new OffscreenCanvas(full.width, full.height);
  paintChunk({ ctx: board.getContext("2d")!, rect, resolution: SWATCH_RESOLUTION }, prepareScene(scene), materials);
  const size = crop.size * SWATCH_RESOLUTION;
  const out = new OffscreenCanvas(size, size);
  out.getContext("2d")!.drawImage(board, crop.x * SWATCH_RESOLUTION, crop.y * SWATCH_RESOLUTION, size, size, 0, 0, size, size);
  const url = URL.createObjectURL(await out.convertToBlob({ type: "image/png" }));
  return { bitmap: await createImageBitmap(out), url, size };
}

/**
 * A session cache of swatches over `paint`. `get` paints at most once per key (concurrent callers
 * share the one paint; a failed paint is forgotten so the next call retries); `peek` answers
 * synchronously with what has landed.
 */
export function createSwatchCache(paint: SwatchPainter) {
  const pending = new Map<string, Promise<Swatch>>();
  const done = new Map<string, Swatch>();
  return {
    get(spec: SwatchSpec): Promise<Swatch> {
      const key = swatchKey(spec);
      let p = pending.get(key);
      if (!p) {
        p = paint(spec).then((s) => {
          done.set(key, s);
          return s;
        });
        p.catch(() => pending.delete(key));
        pending.set(key, p);
      }
      return p;
    },
    peek(spec: SwatchSpec): Swatch | undefined {
      return done.get(swatchKey(spec));
    },
  };
}

/** The app's swatches, painted on the shared atlas. Where there is no `OffscreenCanvas` (a DOM-less
 *  test realm) every paint rejects at once, without fetching the atlas, and callers show no image. */
export const tileSwatches = createSwatchCache(async (spec) => {
  if (typeof OffscreenCanvas === "undefined") throw new Error("tile swatches need OffscreenCanvas");
  return paintSwatch(await sharedMaterials(), spec);
});
